import { gltfLoaderComponentType } from 'src/sdk-components';
import { CONTROLLER_CONSTANTS } from 'src/service/scene/scene-nodes/SceneController/constants';
import {
  getXYZCoordinatesOnCircle,
  getXZCoordinatesOnCircle,
} from 'src/utils/three/getCoordinatesOnCircle';
import { LoadingState } from 'src/service/scene/types';
import * as THREE from 'three';
import { controllerButtonType } from 'src/sdk-components/ControllerButtonComponent';

export class UIController {
  public context: any;

  public xr: any;

  public lever: any;

  public buttonIncrease: any;

  public buttonDecrease: any;

  private clock = new THREE.Clock();

  public visible = true;

  private constructor(lever, buttonIncrease, buttonDecrease, context, xr, onLoaded, onTick) {
    this.lever = lever;
    this.buttonIncrease = buttonIncrease;
    this.buttonDecrease = buttonDecrease;
    this.context = context;
    this.xr = xr;

    this.lever.outputs.onPropertyChanged('loadingState', async (state: any) => {
      if (state === LoadingState.Loaded) {
        lever.pivot.scale.setScalar(0.1);
        lever.pivot.rotation.y = -Math.PI / 2;
        lever.clipActions.forEach((action) => {
          action.setDuration(1);
          action.enabled = true;
          action.paused = true;
          action.time = 1;
          action.setEffectiveWeight(0);
          action.play();
        });
        lever.onTick = () => {
          this.animate();
          if (onTick) onTick();
        };
        if (onLoaded) onLoaded();
      }
    });
  }

  static async create(context, xr, modelNode, onLoaded: any = null, onTick: any = null) {
    const lever = await modelNode.addComponent(gltfLoaderComponentType, {
      url: CONTROLLER_CONSTANTS.URL_LEVER,
      animationTriggerEnabled: true,
      animationEnabled: true,
      animationLoop: false,
      visible: true,
    });

    const buttonIncrease = await modelNode.addComponent(controllerButtonType, {
      type: 'controller',
      size: 0.01,
      src: '/images/green_arrow_up.png',
      localPosition: {
        x: -0.15,
        y: 0.07,
        z: 0.65,
      },
      localRotation: {
        x: 0,
        y: Math.PI / 2,
        z: 0,
      },
      visible: true,
    });

    const buttonDecrease = await modelNode.addComponent(controllerButtonType, {
      type: 'controller',
      size: 0.01,
      src: '/images/red_arrow_down.png',
      localPosition: {
        x: -0.15,
        y: -0.045,
        z: 0.65,
      },
      localRotation: {
        x: 0,
        y: Math.PI / 2,
        z: 0,
      },
      visible: true,
    });

    return new UIController(lever, buttonIncrease, buttonDecrease, context, xr, onLoaded, onTick);
  }

  public setVisible(visible) {
    this.lever.root.visible = visible;
    this.buttonIncrease.inputs.visible = visible;
    this.buttonDecrease.inputs.visible = visible;
  }

  private animate() {
    if (!this.context?.camera || !this.context?.renderer || !this.context.showcase.scene) return;

    const { renderer } = this.context;

    const delta = this.clock.getDelta();
    this.lever.mixer.update(delta);

    if (renderer.xr?.isPresenting) {
      this.animateVR();
    } else {
      this.animateDefault();
    }
  }

  private animateDefault() {
    const { pose } = this.context.showcase.scene;
    const { camera } = this.context;
    const vec3 = new THREE.Vector3();
    const { x, y, z } = getXYZCoordinatesOnCircle(0.85, pose.rotation.x, pose.rotation.y + 180);
    camera.getWorldPosition(vec3);

    this.lever.root.position.set(vec3.x + x, vec3.y + y, vec3.z + z);
    this.lever.root.lookAt(vec3);
    this.lever.pivot.position.set(-0.15, -0.02, 0.65);

    this.buttonIncrease.root.position.set(vec3.x + x, vec3.y + y, vec3.z + z);
    this.buttonIncrease.pivot.position.x = 0;
    this.buttonIncrease.root.lookAt(vec3);

    this.buttonDecrease.root.position.set(vec3.x + x, vec3.y + y, vec3.z + z);
    this.buttonDecrease.pivot.position.x = 0;
    this.buttonDecrease.root.lookAt(vec3);
  }

  private animateVR() {
    const { pose } = this.context.showcase.scene;
    const { x, z } = getXZCoordinatesOnCircle(0.85, pose.rotation.y + 180);

    this.lever.root.position.set(pose.position.x + x, pose.position.y - 0.2, pose.position.z + z);
    this.lever.root.lookAt(pose.position.x, pose.position.y - 0.1, pose.position.z);
    this.lever.pivot.position.set(0, -0.02, 0.65);

    this.buttonIncrease.root.position.set(
      pose.position.x + x,
      pose.position.y - 0.2,
      pose.position.z + z,
    );
    this.buttonIncrease.root.lookAt(pose.position.x, pose.position.y - 0.1, pose.position.z);
    this.buttonIncrease.pivot.position.x = 0.15;

    this.buttonDecrease.root.position.set(
      pose.position.x + x,
      pose.position.y + -0.2,
      pose.position.z + z,
    );
    this.buttonDecrease.pivot.position.x = 0.15;
    this.buttonDecrease.root.lookAt(pose.position.x, pose.position.y - 0.1, pose.position.z);
  }

  public animateUp() {
    const animationUp = this.lever.clipActions[1];
    const animationDown = this.lever.clipActions[0];
    if (animationUp.weight < 1) animationUp.setEffectiveWeight(animationUp.weight + 0.02);
    if (animationDown.weight > 0) animationDown.setEffectiveWeight(animationDown.weight - 0.02);
  }

  public animateDown() {
    const animationUp = this.lever.clipActions[1];
    const animationDown = this.lever.clipActions[0];
    if (animationUp.weight > 0) animationUp.setEffectiveWeight(animationUp.weight - 0.02);
    if (animationDown.weight < 1) animationDown.setEffectiveWeight(animationDown.weight + 0.02);
  }

  animateCenter = () => {
    const animationUp = this.lever.clipActions[1];
    const animationDown = this.lever.clipActions[0];
    if (animationUp.weight > 0) animationUp.setEffectiveWeight(animationUp.weight - 0.02);
    if (animationDown.weight > 0) animationDown.setEffectiveWeight(animationDown.weight - 0.02);
  };
}
